'use client';

// EKA-004 (2026-04-08): Multi-step signup wizard
// Steps:
//   1) Email + password (Supabase auth.signUp)
//   2) NIP + firma (NIP verify -> autofill name/address)
//   3) Bar number + role + confirm
// On finish: POST /api/auth/register -> creates organization + sets user role=admin

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  User,
  Mail,
  Lock,
  Building2,
  Loader2,
  CheckCircle2,
  Hash,
  Scale,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react';
import Button from '@/components/ui/button';
import Input from '@/components/ui/input';
import { createClient } from '@/lib/supabase/client';

type Step = 1 | 2 | 3 | 4;

export default function SignupPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userId, setUserId] = useState('');

  // Step 2
  const [nip, setNip] = useState('');
  const [nipVerified, setNipVerified] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgAddress, setOrgAddress] = useState('');

  // Step 3
  const [barNumber, setBarNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (password.length < 12) {
        throw new Error('Hasło musi mieć min. 12 znaków.');
      }
      const supabase = createClient();
      const { data, error: signUpErr } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo:
            typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
        },
      });
      if (signUpErr) throw signUpErr;
      if (!data.user) throw new Error('Nie udało się utworzyć konta.');
      setUserId(data.user.id);
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd rejestracji.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyNip = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/nip/verify?nip=${encodeURIComponent(nip)}`);
      const data = await res.json();
      if (!data.valid) {
        throw new Error(data.error || 'NIP nieprawidłowy.');
      }
      setNipVerified(true);
      if (data.name && !orgName) setOrgName(data.name);
      if (data.address && !orgAddress) setOrgAddress(data.address);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Weryfikacja NIP nie powiodła się.');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgName.trim()) {
      setError('Nazwa kancelarii jest wymagana.');
      return;
    }
    setError('');
    setStep(3);
  };

  const handleFinalize = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (!acceptTerms) throw new Error('Musisz zaakceptować warunki.');

      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          firm: {
            name: orgName,
            nip,
            address: orgAddress,
            phone,
          },
          barNumber,
          fullName,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Rejestracja nie powiodła się.');

      setStep(4);
      setTimeout(() => router.push('/dashboard'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd rejestracji.');
    } finally {
      setLoading(false);
    }
  };

  const Stepper = () => (
    <div className="mb-6 flex items-center justify-between">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex flex-1 items-center">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
              step >= (n as Step)
                ? 'bg-[#239D9F] text-white'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {step > n ? <CheckCircle2 className="h-4 w-4" /> : n}
          </div>
          {n < 3 && (
            <div
              className={`mx-2 h-0.5 flex-1 ${
                step > n ? 'bg-[#239D9F]' : 'bg-slate-100'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  // SUCCESS
  if (step === 4) {
    return (
      <div className="space-y-4 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-400" />
        <h2 className="text-2xl font-bold text-slate-900">Konto utworzone</h2>
        <p className="text-sm text-slate-500">
          Sprawdź swoją skrzynkę i potwierdź email. Przekierowanie do dashboardu...
        </p>
      </div>
    );
  }

  // STEP 1: Personal + credentials
  if (step === 1) {
    return (
      <form onSubmit={handleStep1} className="space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Załóż kancelarię</h2>
          <p className="mt-1 text-sm text-slate-500">Krok 1 z 3 — Twoje dane</p>
        </div>
        <Stepper />

        {error && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">Imię i nazwisko</label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Jan Nowak"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="pl-10"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">Email służbowy</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input
              type="email"
              placeholder="jan@kancelaria.pl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">
            Hasło (min. 12 znaków)
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input
              type="password"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10"
              required
              minLength={12}
              disabled={loading}
            />
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Dalej <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <p className="text-center text-sm text-slate-500">
          Masz już konto?{' '}
          <Link href="/login" className="font-medium text-[#239D9F] hover:text-[#11305A]">
            Zaloguj się
          </Link>
        </p>
      </form>
    );
  }

  // STEP 2: NIP + firma
  if (step === 2) {
    return (
      <form onSubmit={handleStep2} className="space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dane kancelarii</h2>
          <p className="mt-1 text-sm text-slate-500">Krok 2 z 3 — Weryfikacja firmy</p>
        </div>
        <Stepper />

        {error && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">NIP</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="1234567890"
                value={nip}
                onChange={(e) => {
                  setNip(e.target.value.replace(/\D/g, ''));
                  setNipVerified(false);
                }}
                className="pl-10"
                maxLength={10}
                disabled={loading}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={handleVerifyNip}
              disabled={loading || nip.length !== 10}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sprawdź'}
            </Button>
          </div>
          {nipVerified && (
            <p className="flex items-center gap-1 text-xs text-emerald-400">
              <CheckCircle2 className="h-3 w-3" /> NIP zweryfikowany
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">Nazwa kancelarii</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Kancelaria Adwokacka Nowak i Partnerzy"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="pl-10"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-600">Adres</label>
          <Input
            type="text"
            placeholder="ul. Przykładowa 1, 00-001 Warszawa"
            value={orgAddress}
            onChange={(e) => setOrgAddress(e.target.value)}
            disabled={loading}
          />
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setStep(1)}
            disabled={loading}
            className="flex-1"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Wstecz
          </Button>
          <Button type="submit" disabled={loading} className="flex-1">
            Dalej <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </form>
    );
  }

  // STEP 3: Bar number + finalize
  return (
    <form onSubmit={handleFinalize} className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Profil zawodowy</h2>
        <p className="mt-1 text-sm text-slate-500">Krok 3 z 3 — Finalizacja</p>
      </div>
      <Stepper />

      {error && <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-600">
          Numer wpisu na listę adwokatów / radców (opcjonalny)
        </label>
        <div className="relative">
          <Scale className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
          <Input
            type="text"
            placeholder="ADW/WAR/12345"
            value={barNumber}
            onChange={(e) => setBarNumber(e.target.value)}
            className="pl-10"
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-600">Telefon kontaktowy</label>
        <Input
          type="tel"
          placeholder="+48 600 000 000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={loading}
        />
      </div>

      <label className="flex items-start gap-2 text-xs text-slate-500">
        <input
          type="checkbox"
          checked={acceptTerms}
          onChange={(e) => setAcceptTerms(e.target.checked)}
          className="mt-0.5"
          disabled={loading}
        />
        <span>
          Akceptuję regulamin, politykę prywatności (RODO) oraz wymóg zachowania tajemnicy
          adwokackiej / radcy prawnego zgodnie z art. 6 Prawa o adwokaturze.
        </span>
      </label>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep(2)}
          disabled={loading}
          className="flex-1"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Wstecz
        </Button>
        <Button
          type="submit"
          disabled={loading || !acceptTerms}
          className="flex-1"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Załóż kancelarię
        </Button>
      </div>
    </form>
  );
}
